import React from "react";
import { alpha } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import Chip from "@mui/material/Chip";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";

import { wall, spotsLabel, spotsColour } from "../upfrontSessionsHelpers";

function SessionCard({ session: c, isPicked, disabled, reason, paid, showLevel, onToggle, onCancel, cardRef }) {
  const meta = [c.duration ? `${c.duration} min` : null, c.trainer?.name].filter(Boolean).join(" · ");
  const levelLabel = showLevel ? c.levelRequiredNames : null;

  return (
    <Card
      ref={cardRef}
      variant="outlined"
      sx={{
        position: "relative",
        minHeight: "5.5rem",
        ...(isPicked && {
          borderColor: "primary.main",
          borderWidth: 1.5,
          bgcolor: (t) => alpha(t.palette.primary.main, 0.05)
        })
      }}
    >
      <CardActionArea disabled={disabled} onClick={onToggle} sx={{ p: 1.5, height: "100%", opacity: disabled ? 0.55 : 1 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
          {isPicked ? (
            <CheckCircleIcon sx={{ color: "primary.main" }} />
          ) : (
            <RadioButtonUncheckedIcon sx={{ color: disabled ? "text.disabled" : "text.secondary" }} />
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" fontWeight={700}>
              {wall(c.startDate).toFormat("ccc d LLL")} · {c.time}
            </Typography>
            <Typography variant="caption" color="textSecondary" component="div">
              {meta}
              {/* Clubs run the same class at several venues — where it is decides
                  whether the client can make it, so it carries the emphasis. */}
              {c.location?.name && (
                <>
                  {meta ? " · " : ""}
                  <Box component="span" sx={{ fontWeight: 700, color: "text.primary" }}>
                    {c.location.name}
                  </Box>
                </>
              )}
              {spotsLabel(c) && (
                <>
                  {meta || c.location?.name ? " · " : ""}
                  {/* Classes fill up, and a session left for later is not guaranteed. */}
                  <Box component="span" sx={{ color: spotsColour(c), fontWeight: spotsColour(c) === "text.secondary" ? 400 : 700 }}>
                    {spotsLabel(c)}
                  </Box>
                </>
              )}
            </Typography>
            {(levelLabel || reason || paid) && (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.75, pr: onCancel ? "6rem" : 0 }}>
                {levelLabel && (
                  <Chip size="small" label={levelLabel} variant="outlined" color="warning" sx={{ height: "1.25rem", fontSize: "0.65rem" }} />
                )}
                {reason && <Chip size="small" label={reason} variant="outlined" sx={{ height: "1.25rem", fontSize: "0.65rem" }} />}
                {/* Greyed out like every other taken seat, so without this the client
                    can't tell a class they've paid for from one they still owe on. */}
                {paid && (
                  <Chip
                    size="small"
                    label="Paid"
                    color="success"
                    sx={{ height: "1.25rem", fontSize: "0.65rem", fontWeight: 700, color: "common.white" }}
                  />
                )}
              </Box>
            )}
          </Box>
        </Box>
      </CardActionArea>
      {/* Still offered on a card faded out by the pack window — a booking this
          pack can't pay for is exactly the one a client may want to drop. Sits in
          the badge row's corner as one more chip, outside the action area (no
          nested buttons), so the card keeps the height of its neighbours. */}
      {onCancel && (
        <Chip
          size="small"
          label="Cancel booking"
          variant="outlined"
          color="error"
          icon={<CloseIcon />}
          onClick={onCancel}
          sx={{
            position: "absolute",
            right: "0.75rem",
            bottom: "0.75rem",
            height: "1.25rem",
            fontSize: "0.65rem",
            "& .MuiChip-icon": { fontSize: "0.8rem", ml: 0.5 }
          }}
        />
      )}
    </Card>
  );
}

export default SessionCard;
